import { ApiClient } from "@ucl/library";
import { AddressFieldValue } from "@ucl/library";

const toURLSearchParamsObject = (address: Partial<AddressFieldValue>) => {
  const params: Record<string, string> = {};

  if (address.street) params.street = address.street;
  if (address.street2) params.street2 = address.street2;
  if (address.city) params.city = address.city;
  if (address.state) params.state = address.state.toString();
  if (address.zip) params.zip = address.zip;

  return params;
};

class AddressApiClient {
  private _apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this._apiClient = apiClient;
  }

  /**
   * Gets filtered addresses by query with state filter applied.
   */
  getFilteredAddressesByQuery = async (address: Partial<AddressFieldValue>) => {
    return this._apiClient.post<AddressFieldValue[]>(
      `api/address/many`,
      new URLSearchParams({
        ...toURLSearchParamsObject(address),
        stateFilter: ["CA", "OR"].join(","), // Default state filter
      }),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );
  };

  /**
   * Gets addresses by query without state filtering.
   */
  getAddressesByQuery = async (address: Partial<AddressFieldValue>) => {
    return this._apiClient.post<AddressFieldValue[]>(
      `api/address/many`,
      new URLSearchParams(toURLSearchParamsObject(address)),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );
  };

  /**
   * Gets a single address by query.
   */
  getAddressByQuery = async (queryAddress: AddressFieldValue) => {
    return this._apiClient.post<AddressFieldValue>(
      `api/address/one`,
      new URLSearchParams(toURLSearchParamsObject(queryAddress)),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );
  };

  /**
   * Gets addresses by query using GET method (alternative to POST).
   */
  getAddressesByQueryGet = async (address: Partial<AddressFieldValue>) => {
    if (typeof address.state === "number") {
      address.state = address.state.toString();
    }

    return this._apiClient.get<AddressFieldValue[]>(
      `api/addresses?${new URLSearchParams({
        ...(address.street && { street: address.street }),
        ...(address.street2 && { street2: address.street2 }),
        ...(address.city && { city: address.city }),
        ...(address.state && { state: address.state }),
        ...(address.zip && { zip: address.zip }),
      })}`
    );
  };

  /**
   * Gets a single address by query using GET method (alternative to POST).
   */
  getAddressByQueryGet = async (queryAddress: AddressFieldValue) => {
    if (typeof queryAddress.state === "number") {
      queryAddress.state = queryAddress.state.toString();
    }

    const params = new URLSearchParams({
      ...(queryAddress.street && { street: queryAddress.street }),
      ...(queryAddress.street2 && { street2: queryAddress.street2 }),
      ...(queryAddress.city && { city: queryAddress.city }),
      ...(queryAddress.state && { state: queryAddress.state }),
      ...(queryAddress.zip && { zip: queryAddress.zip }),
    });

    return this._apiClient.get<AddressFieldValue>(`api/address?${params}`);
  };
}

export default AddressApiClient;
